import React, { Component, Fragment } from "react";
import { ReactTabulator } from "react-tabulator";
import { FormControl } from "react-bootstrap";
import "react-tabulator/lib/styles.css";
import "react-tabulator/lib/css/tabulator.min.css";
import Header from "../header";
import {
  getClientDetailsArchive,
  getArchivalData,
  getAllRepoArchivalData,
  updateArchiveInfo,
} from "../../libs/API";
import { role } from "../enum";
import "./archive.css";
import EmailPopup from "../emailpopup";

class ArchiveRepository extends Component {
  ref = null;

  constructor(props) {
    super(props);
    this.state = {
      repositories: [],
      clientData: null,
      clientId: this.props.props.clientId ?? "",
      selectedClientId: this.props.props.clientId ?? "",
      hasManageClientsUserRole: false,
      isLoading: false,
      error: null,
      filterFrom: "", // YYYY-MM-DD
      filterTo: "",
    };
  }

  async componentDidMount() {
    await this.populateClient();
    const canManageClients = this.props.props.groups.includes(
      role.documentSync_ManageClients
    );
    if (canManageClients) {
      await this.fetchFullArchivalData();
    }
  }

  async populateClient() {
    try {
      const hasManageClientsUserRole = this.props.props.groups.includes(
        role.documentSync_ManageClients
      );

      if (
        this.state.clientId &&
        this.state.clientId > 0 &&
        !hasManageClientsUserRole
      ) {
        // For non-admin users, directly fetch repositories for their client
        await this.fetchArchivalData(this.state.clientId);
      } else {
        // For admin users, fetch client list
        const data = await getClientDetailsArchive(true);
        this.setState({
          clientData: data,
          hasManageClientsUserRole,
          selectedClientId: "-1",
        });
      }
    } catch (error) {
      console.error("Error populating client details:", error);
      this.setState({
        error: "Failed to load client details",
        isLoading: false,
      });
    }
  }

  async fetchArchivalData(clientId) {
    this.setState({ isLoading: true, error: null });
    try {
      const response = await getArchivalData(clientId);
      if (!response) {
        throw new Error("Failed to fetch repository data");
      }
      const formattedData = response.map((repo) => ({
        owner: repo.owner,
        folderName: repo.folderName,
        s3Path: repo.s3Path || "N/A",
        importedDate: repo.importedDate ? new Date(repo.importedDate) : null,
        lastArchivedDate: repo.lastArchivedDate
          ? new Date(repo.lastArchivedDate)
          : null,
        repositoryId: repo.repositoryId,
        clientId: repo.clientId,
        clientContactforArchives: repo.clientContactforArchives,
        clientContactforArchivesEmail: repo.clientContactforArchivesEmail,
      }));
      this.setState({ repositories: formattedData, error: null }, () => {
        if (this.ref && this.ref.table) {
          this.ref.table.setData(formattedData);
        }
      });
    } catch (error) {
      console.error("Error fetching archival data:", error);
      this.setState({
        error: error.message || "Failed to load repository data",
        repositories: [],
      });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  async fetchFullArchivalData() {
    this.setState({ isLoading: true, error: null });
    try {
      const response = await getAllRepoArchivalData();
      if (!response) {
        throw new Error("Failed to fetch repository data");
      }
      const formattedData = response.map((repo) => ({
        owner: repo.owner,
        folderName: repo.folderName,
        s3Path: repo.s3Path || "N/A",
        importedDate: repo.importedDate ? new Date(repo.importedDate) : null,
        lastArchivedDate: repo.lastArchivedDate
          ? new Date(repo.lastArchivedDate)
          : null,
        repositoryId: repo.repositoryId,
        clientId: repo.clientId,
        clientName: repo.clientName,
        clientContactforArchives: repo.clientContactforArchives,
        clientContactforArchivesEmail: repo.clientContactforArchivesEmail,
      }));
      this.setState({ repositories: formattedData, error: null }, () => {
        if (this.ref && this.ref.table) {
          this.ref.table.setData(formattedData);
        }
      });
    } catch (error) {
      console.error("Error fetching archival data:", error);
      this.setState({
        error: error.message || "Failed to load repository data",
        repositories: [],
      });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  onClientChange = async (e) => {
    const clientId = e.target.value;
    this.setState({ selectedClientId: clientId });
    if (clientId || clientId === -1) {
      await this.fetchArchivalData(clientId);
    } else {
      this.setState({ repositories: [] });
    }
  };

  dateFormatter = (cell) => {
    const v = cell.getValue();
    if (!v) return ""; // empty for null/undefined
    // v is already a Date instance (per our mapping),
    // but if it could ever be a string, you can normalize:
    const date = v instanceof Date ? v : new Date(v);
    return this.getFormattedDate(date);
  };

  getFormattedDate = (date) => {
    const pad = (n) => n.toString().padStart(2, "0");
    const year = date.getUTCFullYear();
    const month = pad(date.getUTCMonth() + 1);
    const day = pad(date.getUTCDate());
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());
    return `${month}/${day}/${year} ${hours}:${minutes}`;
  };

  openButton = () =>
    "<div><button class='btn btn-outline-secondary btn-sm updatedeletebuttons'>Archive Folder</button></div>";

  // date range filter handlers
  handleFilterChange = (field) => (e) => {
    this.setState({ [field]: e.target.value }, this.applyDateFilter);
  };

  applyDateFilter = () => {
    const { filterFrom, filterTo } = this.state;

    if (!filterFrom && !filterTo) {
      this.ref.table.clearFilter();
      return;
    }

    const filters = [];

    if (filterFrom) {
      filters.push({
        field: "importedDate",
        type: ">=", // no trailing space
        value: new Date(filterFrom),
      });
    }
    if (filterTo) {
      const toDate = new Date(filterTo);
      toDate.setDate(toDate.getDate() + 1); // include entire “to” day
      filters.push({
        field: "importedDate",
        type: "<",
        value: toDate,
      });
    }

    this.ref.table.setFilter(filters);
  };

  downloadCSV = (e) => {
    e.preventDefault();
    this.ref.table.download("csv", "Archival Data.csv", { delimiter: "|" });
  };

  onCellEdited = async (cell) => {
    const updated = cell.getRow().getData();

    // 1) Reflect in local state so the UI stays in sync:
    this.setState((state) => ({
      repositories: state.repositories.map((r) =>
        r.repositoryId === updated.repositoryId ? updated : r
      ),
    }));

    // 2) Persist to the back-end:
    try {
      await updateArchiveInfo(
        updated.repositoryId,
        updated.clientContactforArchives?.trim() || "",
        updated.clientContactforArchivesEmail?.trim() || ""
      );
    } catch (err) {
      console.error("Failed to save client info:", err);
      // optionally show a toast or revert local state…
    }
  };

  // table columns
  columns = [
    {
      title: "Client Name",
      field: "clientName",
      sorter: "string",
      headerFilter: true,
    },
    {
      title: "Client Contact for Archives",
      field: "clientContactforArchives",
      sorter: "string",
      headerFilter: true,
      editor: "input",
    },
    {
      title: "Client Contact for Archives Email Address",
      field: "clientContactforArchivesEmail",
      sorter: "string",
      headerFilter: true,
      editor: "input",
    },
    {
      title: "Repository Owner",
      field: "owner",
      sorter: "string",
      headerFilter: true,
    },
    {
      title: "Repository Name",
      field: "folderName",
      sorter: "string",
      headerFilter: true,
    },
    {
      title: "",
      field: "s3Path",
      sorter: "string",
      headerFilter: true,
      formatter: this.openButton,
      cellClick: (e, cell) =>
        this.setState({
          showEmailPopup: true,
          rowData: cell.getRow().getData(),
        }),
    },
    {
      title: "Import Date",
      field: "importedDate",
      sorter: (a, b) => {
        if (!a && !b) return 0;
        if (!a) return -1;
        if (!b) return 1;
        return a.getTime() - b.getTime();
      },
      headerFilter: true,
      formatter: this.dateFormatter,
    },
    {
      title: "Archived Date",
      field: "lastArchivedDate",
      sorter: (a, b) => {
        if (!a && !b) return 0;
        if (!a) return -1;
        if (!b) return 1;
        return a.getTime() - b.getTime();
      },
      headerFilter: true,
      formatter: this.dateFormatter,
    },
  ];

  // table options
  options = {
    layout: "fitColumns",
    responsiveLayout: "collapse",
    pagination: "local",
    paginationSize: 10,
    paginationSizeSelector: [10, 20, 50, 100],
    movableColumns: true,
    placeholder: "No Data Available",
    downloadReady: (fileContents, blob) => blob,
  };

  renderEmailPop() {
    return (
      this.state.showEmailPopup && (
        <EmailPopup
          props={{
            showEmailPopup: this.state.showEmailPopup,
            isArchivePopup: true,
            rowData: this.state.rowData,
            selectedClientId: this.state.selectedClientId,
          }}
          unmountMe={() => this.setState({ showEmailPopup: false })}
        />
      )
    );
  }

  render() {
    const {
      hasManageClientsUserRole,
      clientData,
      selectedClientId,
      repositories,
      isLoading,
      error,
      filterFrom,
      filterTo,
    } = this.state;

    const visibleColumns = this.columns.filter((col) => {
      if (col.field === "clientName") {
        return Number(selectedClientId) === -1;
      }
      return true;
    });

    return (
      <Fragment>
        <Header props={this.props.props} history={this.props.history} />
        <div className="archive-container">
          <div className="archive-header" style={{ width: "90%" }}>
            <h4>Repository Archive</h4>
          </div>

          <div className="archive-content">
            {/* TOOLBAR */}
            <div
              className="table-controls"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "1rem",
                margin: "10px 0",
              }}
            >
              {/* Export CSV */}
              <button
                className="btn btn-primary"
                onClick={this.downloadCSV}
                disabled={!repositories.length}
              >
                Export CSV
              </button>

              {/* Date Range */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <label style={{ margin: 0, whiteSpace: "nowrap" }}>From:</label>
                <FormControl
                  type="date"
                  value={filterFrom}
                  onChange={this.handleFilterChange("filterFrom")}
                  style={{ width: "140px" }}
                />
                <label style={{ margin: 0, whiteSpace: "nowrap" }}>To:</label>
                <FormControl
                  type="date"
                  value={filterTo}
                  onChange={this.handleFilterChange("filterTo")}
                  style={{ width: "140px" }}
                />
              </div>

              {/* Client Dropdown */}
              {hasManageClientsUserRole && (
                <FormControl
                  componentClass="select"
                  value={selectedClientId}
                  onChange={this.onClientChange}
                  style={{ width: "200px", alignSelf: "center" }}
                >
                  <option value="-1">All</option>
                  {clientData?.map((c) => (
                    <option key={c.clientId} value={c.clientId}>
                      {c.clientName}
                    </option>
                  ))}
                </FormControl>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}
            {isLoading ? (
              <div className="loading-message">Loading repositories…</div>
            ) : (
              <ReactTabulator
                ref={(ref) => (this.ref = ref)}
                columns={visibleColumns}
                data={repositories}
                options={this.options}
                cellEdited={this.onCellEdited}
              />
            )}
          </div>
        </div>

        {this.state.showEmailPopup && this.renderEmailPop()}
      </Fragment>
    );
  }
}

export default ArchiveRepository;
